
import React, { useState, useEffect, useMemo } from "react";
import { useTournament } from "@/hooks/use-tournament";
import { useLanguage } from "@/hooks/use-language";
import { SharedResultsEntry } from "./shared/SharedResultsEntry";
import { Leaderboard } from "./mexicano/Leaderboard";
import { MexicanoAdapter } from "@/adapters/tournament-adapters";
import { TournamentHeader } from "./shared/TournamentHeader";
import { BenchPanel } from "./shared/BenchPanel";
import { RoundHistoryDialog } from "./RoundHistoryDialog";

interface MexicanoTournamentProps {
  participants: any[];
  onBack?: () => void;
}

export const MexicanoTournament: React.FC<MexicanoTournamentProps> = ({ participants, onBack }) => {
  const { t, language } = useLanguage();
  const { 
    tournamentState, 
    resetTournament,
    showLeaderboard,
    setShowLeaderboard,
    goToPreviousRound,
    submitRoundResults
  } = useTournament();

  // Create adapter for shared component using useMemo to ensure it updates when tournament state changes
  const mexicanoAdapter = useMemo(() => 
    new MexicanoAdapter(tournamentState, submitRoundResults), 
    [tournamentState, submitRoundResults]
  );
  
  const [showHistory, setShowHistory] = useState(false);

  // Ensure leaderboard renders whenever the tournament is finished
  const shouldShowLeaderboard = tournamentState.isFinished || showLeaderboard;
  const showBackButton = !shouldShowLeaderboard && (tournamentState.isRoundActive || tournamentState.isFinished);

  // Log rendering decisions (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🎨 MexicanoTournament render state:', {
        showLeaderboard,
        shouldShowLeaderboard,
        isRoundActive: tournamentState.isRoundActive,
        isFinished: tournamentState.isFinished,
        isActive: tournamentState.isActive,
        currentRound: tournamentState.currentRound,
        totalRounds: tournamentState.totalRounds,
      });
    }
  }, [
    showLeaderboard,
    shouldShowLeaderboard,
    tournamentState.isRoundActive,
    tournamentState.isFinished,
    tournamentState.isActive,
    tournamentState.currentRound,
    tournamentState.totalRounds
  ]);

  const handleBack = () => {
    // If we're in round 1, go back to front page
    if (tournamentState.currentRound === 1) {
      if (onBack) {
        onBack();
      } else {
        resetTournament();
      }
    } else {
      // If we're in round 2+, go to previous round
      if (goToPreviousRound) {
        goToPreviousRound();
      }
    }
  };

  return (
    <div className="mt-8 border-t pt-6">
      <div className="space-y-6">
        <TournamentHeader
          title={tournamentState.isFinished
            ? (language === 'no' ? 'Mexicano Turnering Ferdig' : 'Mexicano Tournament Finished')
            : (language === 'no' ? `Mexicano Runde ${tournamentState.currentRound}` : `Mexicano Round ${tournamentState.currentRound}`)}
          language={language}
          showBack={showBackButton}
          onBack={handleBack}
          showLeaderboard={shouldShowLeaderboard}
          onToggleLeaderboard={() => {
            if (tournamentState.isFinished) return;
            setShowLeaderboard(!showLeaderboard);
          }}
          onHistory={() => setShowHistory(true)}
        />
        
        {/* Show bench panel - works for both Americano and Mexicano */}
        <BenchPanel />
        
        {shouldShowLeaderboard ? (
          <Leaderboard />
        ) : tournamentState.isRoundActive ? (
          <SharedResultsEntry 
            adapter={mexicanoAdapter}
            currentRound={tournamentState.currentRound}
            totalRounds={tournamentState.totalRounds}
          />
        ) : (
          <Leaderboard />
        )}
      </div>
      <RoundHistoryDialog open={showHistory} onOpenChange={setShowHistory} />
    </div>
  );
};
